import { useSession, useSignOut } from '@church/data'
import {
  faChevronRight,
  faChurch,
  faChurchDuotone,
  faCircleUser,
  faMusicDuotone,
  faRightFromBracket,
  faTowerBroadcastDuotone,
  faTriangleExclamation,
  faUserGear,
} from '@church/ui-icons'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { usesSeat } from '@church/permissions'
import { z } from 'zod'
import { Button, Icon } from '../components/ui.tsx'
import { signInUrl, urlOn } from '../lib/site.ts'

/**
 * id.forhim.io's home: who is signed in, and the ForHim apps to open. A person
 * with no church yet is sent to create one (a platform operator is not).
 */
export const Route = createFileRoute('/_authed/apps')({
  // `stuck`: an app that kept sending them back to sign in (the bounce guard, lib/site.ts).
  validateSearch: z.object({ stuck: z.string().optional() }),
  beforeLoad: ({ context }) => {
    if (context.me.memberships.length === 0 && !context.me.platformOperator)
      throw redirect({ to: '/onboarding' })
  },
  component: AppsScreen,
})

const APPS = [
  {
    site: 'church',
    name: 'ForChurch',
    icon: faChurchDuotone,
    what: 'People, groups, giving, check-in, messages, events and insights',
  },
  {
    site: 'services',
    name: 'ForServices',
    icon: faMusicDuotone,
    what: 'Service plans, songs, schedules, Music Stand',
  },
  {
    site: 'stream',
    name: 'ForStream',
    icon: faTowerBroadcastDuotone,
    what: 'Live streaming, the video library, captions',
  },
] as const

function AppsScreen() {
  const { me } = useSession()
  const { stuck } = Route.useSearch()
  const stuckHost = URL.canParse(stuck ?? '') ? new URL(stuck ?? '').host : null
  const signOut = useSignOut()
  // Only the apps they can use: ForServices and ForStream follow the church's
  // plan, and ForStream is for its staff.
  const features = me?.entitlements?.features ?? []
  const roles =
    me?.memberships.find((m) => m.organizationId === me.activeOrganizationId)?.roles ?? []
  const usable = APPS.filter(
    (app) =>
      app.site === 'church' ||
      (app.site === 'services' && features.includes('services')) ||
      (app.site === 'stream' && features.includes('stream') && usesSeat(roles)),
  )
  // Their home app first (ForServices for Sunday-only volunteers).
  const ordered =
    me?.home === 'services'
      ? [
          ...usable.filter((a) => a.site === 'services'),
          ...usable.filter((a) => a.site !== 'services'),
        ]
      : usable
  const apps = me?.platformOperator
    ? [
        ...ordered,
        {
          site: 'admin',
          name: 'Admin',
          icon: faUserGear,
          what: 'Every church, plans and billing',
        } as const,
      ]
    : ordered

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="mb-8 flex flex-col items-center text-center">
        <span className="flex size-12 items-center justify-center rounded-2xl bg-brand-solid text-brand-on-solid">
          <Icon icon={faChurch} className="size-6" />
        </span>
        <span className="mt-3 text-2xl font-bold text-content-default">ForHim</span>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-content-muted">
          <Icon icon={faCircleUser} className="size-3.5" />
          <span className="font-medium break-all">{me?.user.email}</span>
        </p>
      </div>
      {stuckHost && (
        <p
          role="alert"
          className="mb-4 flex w-full max-w-md gap-2 rounded-lg bg-warning-soft px-4 py-3 text-sm text-warning-content"
        >
          <Icon icon={faTriangleExclamation} className="mt-0.5 size-4" />
          You are signed in, but {stuckHost} kept asking you to sign in. Sign out of every app below
          and sign in again; if it happens again, tell us.
        </p>
      )}
      <ul className="grid w-full max-w-md gap-3">
        {apps.map((app) => (
          <li key={app.site}>
            <a
              href={urlOn(app.site, '/')}
              className="flex items-center gap-4 rounded-xl border border-border-subtle bg-surface-raised p-4 no-underline shadow-sm hover:border-border-strong sm:p-5"
            >
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-content-brand">
                <Icon icon={app.icon} className="size-6" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-base font-semibold text-content-default">
                  {app.name}
                </span>
                <span className="mt-0.5 block text-sm text-content-muted">{app.what}</span>
              </span>
              <Icon icon={faChevronRight} className="size-3.5 text-content-subtle" />
            </a>
          </li>
        ))}
      </ul>
      <Button
        variant="secondary"
        icon={faRightFromBracket}
        className="mt-6"
        disabled={signOut.isPending}
        onClick={() =>
          signOut.mutate(undefined, { onSettled: () => window.location.assign(signInUrl('/apps')) })
        }
      >
        Sign out of every app
      </Button>
    </div>
  )
}
